import { useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { recordActivity, showWarning } from '../features/inactivity/inactivitySlice';

const INACTIVITY_LIMIT_MS = 5 * 60 * 1000; // 5 minutes → show warning modal
const THROTTLE_MS        = 30 * 1000;       // dispatch recordActivity at most once per 30 s

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

const useAutoLogout = () => {
  const dispatch   = useDispatch();
  const { user }   = useSelector((state) => state.auth);
  const { lastActivity, warningVisible } = useSelector((state) => state.inactivity);

  // Mirror warningVisible in a ref so the event handler can read it
  // without a stale closure (avoids re-attaching listeners on every modal toggle).
  const warningVisibleRef = useRef(warningVisible);
  useEffect(() => {
    warningVisibleRef.current = warningVisible;
  }, [warningVisible]);

  // ── Timer: fire showWarning when 5 min of silence have elapsed ──────────────
  // Re-runs whenever lastActivity changes (i.e. after each throttled dispatch)
  // or when warningVisible changes (pauses while modal is open).
  useEffect(() => {
    if (!user || warningVisible) return;

    const elapsed   = Date.now() - lastActivity;
    const remaining = INACTIVITY_LIMIT_MS - elapsed;

    if (remaining <= 0) {
      dispatch(showWarning());
      return;
    }

    const timer = setTimeout(() => dispatch(showWarning()), remaining);
    return () => clearTimeout(timer);
  }, [user, lastActivity, warningVisible, dispatch]);

  // ── Activity handler (throttled) ─────────────────────────────────────────────
  const lastDispatchRef = useRef(0);

  const handleActivity = useCallback(() => {
    if (warningVisibleRef.current) return; // modal is open — only "I'm still here" can reset

    const now = Date.now();
    if (now - lastDispatchRef.current < THROTTLE_MS) return;
    lastDispatchRef.current = now;

    dispatch(recordActivity());
  }, [dispatch]);

  // ── Attach / detach listeners ─────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, handleActivity, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, handleActivity));
  }, [user, handleActivity]);
};

export default useAutoLogout;
